import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const VybePage = () => {
  const [showShootingForm, setShowShootingForm] = useState(false);
  const [showCollabForm, setShowCollabForm] = useState(false);
  const [showSubscriptionForm, setShowSubscriptionForm] = useState(false);
  const [isLoading, setIsLoading] = useState<string | null>(null);
  
  const [shootingFormData, setShootingFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    typeShoot: 'Standard',
    date: '',
    message: '',
  });

  const [collabFormData, setCollabFormData] = useState({
    nom: '',
    email: '',
    entreprise: '',
    secteur: '',
    typeCollab: 'Story Instagram',
    budget: '',
    message: '',
  });

  const [subscriptionFormData, setSubscriptionFormData] = useState({
    nom: '',
    email: '',
    entreprise: '',
    secteur: '',
    pack: 'Pack Starter',
    message: '',
  });

  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fonction pour gérer l'achat des presets mobiles - OPTIMISÉE
  const handleBuyNow = async (product: { id: string; name: string; price: number }) => {
    setIsLoading(product.id);
    
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-payment-optimized`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          productName: product.name,
          price: product.price,
          quantity: 1,
          productType: 'digital', // Produit numérique
          description: 'Pack Ultimate Mobile - 50+ Presets Mobile des Stars + Tutoriels + Installation 1-Click + Accès à vie',
        }),
      });

      const data = await response.json();
      
      if (data.url) {
        // Optimisation : préchargement de la page de succès
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = '/success';
        document.head.appendChild(link);
        
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Erreur lors de la création du paiement');
      }
    } catch (error) {
      console.error('Erreur:', error);
      
      // Affichage d'erreur plus user-friendly
      const errorMessage = error.message.includes('network') 
        ? 'Problème de connexion. Vérifiez votre internet et réessayez.'
        : 'Erreur lors du paiement. Veuillez réessayer dans quelques instants.';
        
      alert(errorMessage);
    } finally {
      setIsLoading(null);
    }
  };

  // Fonction pour gérer les réservations (shootings, collaborations)
  const handleBooking = (type: string, serviceName: string, price: number) => {
    if (type === 'shooting') {
      setShowShootingForm(true);
    } else if (type === 'collaboration') {
      setShowCollabForm(true);
    } else if (type === 'formation') {
      handleBuyFormation({ name: serviceName, price });
    }
  };

  // Abonnements Marques/Restaurants
  const subscriptionPacks = [
    {
      name: 'Pack Starter',
      price: 140,
      period: '/mois',
      description: '2-3 micro influenceurs (3K → 20K) chaque semaine + stories et posts de présence',
      features: ['2-3 micro influenceurs', '3K à 20K abonnés', 'Stories + posts', 'Rapport mensuel'],
      popular: false,
      badge: 'IDÉAL DÉBUTANT',
      badgeColor: 'from-blue-500 to-cyan-500',
      originalPrice: 190,
    },
    {
      name: 'Pack Business',
      price: 390,
      originalPrice: 590,
      period: '/mois',
      description: '6-8 influenceurs + 2 reels/TikToks + shooting photo inclus',
      features: [
        '6-8 influenceurs sélectionnés',
        '10K à 100K abonnés',
        '3 visites par semaine',
        '2 Reels ou TikToks mensuels',
        'Shooting photo professionnel',
        'Gestion réseaux sociaux',
        'Analytics et ROI tracking',
        '🔥 EXCLUSIF : Shooting mensuel avec Adel Saï (@Adofficielo)',
        '📱 5.4M TikTok • 2.6M Instagram • 1.6M Facebook',
        '🎬 Reel + TikTok avec mention collab garantis'
      ],
      popular: true,
      badge: 'MEILLEURE OFFRE + ADEL SAÏ',
      badgeColor: 'from-orange-500 to-red-500',
    },
    {
      name: 'Pack Premium',
      price: 790,
      period: '/mois',
      description: '12-15 influenceurs jusqu\'à 500K + vidéos montées + événements',
      features: [
        '12-15 influenceurs premium',
        'Jusqu\'à 500K abonnés',
        '4-5 visites par semaine',
        'Vidéos montées professionnelles',
        'Organisation d\'événements',
        'Collaborations avec célébrités',
        'Stratégie marketing complète',
        'Support dédié 7j/7'
      ],
      popular: false,
      badge: 'CROISSANCE RAPIDE',
      badgeColor: 'from-purple-500 to-pink-500',
      originalPrice: 1190,
    },
    {
      name: 'Pack Élite',
      price: 1590,
      period: '/mois',
      description: 'Macro influenceurs (500K → 5M), direction artistique et équipe Community Manager dédiée',
      features: [
        'Macro influenceurs 500K-5M',
        'Célébrités et stars confirmées',
        'Visites quotidiennes possibles',
        'Direction artistique complète',
        'Équipe Community Manager dédiée',
        'Gestion complète réseaux sociaux',
        'Campagnes virales sur mesure',
        'Couverture médiatique',
        'Partenariats exclusifs',
        'Équipe dédiée 24h/24',
        'Garantie résultats'
      ],
      popular: false,
      badge: 'PRESTIGE MAXIMUM',
      badgeColor: 'from-yellow-500 to-orange-500',
      originalPrice: 2390,
    }
  ];

  // Shootings Photo & Vidéo
  const shootingServices = [
    {
      name: 'Shooting Standard',
      price: 180,
      duration: '1h',
      includes: ['10 photos HD', '1 vidéo', 'Retouches basiques'],
    },
    {
      name: 'Flying Dress',
      price: 280,
      duration: '1h30',
      includes: ['Robe fournie', '10 photos HD', 'Clip cinématique', 'Retouches premium'],
    },
    {
      name: 'Couple / Lifestyle',
      price: 250,
      duration: '1h30',
      includes: ['15 photos HD', '1 vidéo', 'Poses dirigées', 'Retouches premium'],
    },
    {
      name: 'Pack Luxe Créateur',
      price: 350,
      duration: '2h',
      includes: ['Direction artistique', 'Photo + vidéo', 'Retouches premium', 'Livraison 48h'],
    },
  ];

  // Formations & Presets
  const formations = [
    {
      name: 'Formation Influenceur Pro',
      price: 89,
      description: 'Devenez un influenceur professionnel',
    },
    {
      name: 'SECRETS D\'INFLUENCE MILLIONAIRE',
      price: 197,
      description: 'La méthode EXACTE pour passer de 0 à 100K€/an avec l\'influence - Révélations exclusives d\'un créateur à 7M d\'abonnés',
    },
    {
      name: 'Pack Lancement',
      price: 299,
      description: 'Coaching + preset + media kit complet',
    },
  ];

  // Collaborations avec les Marques
  const collabServices = [
    {
      name: 'Story Instagram',
      price: 800,
      description: 'Story 24h avec mention et tag',
    },
    {
      name: 'Post + Story',
      price: 1000,
      description: 'Post permanent + story avec engagement',
    },
    {
      name: 'Vidéo Reel / TikTok',
      price: 1500,
      description: 'Contenu vidéo créatif et engageant',
    },
    {
      name: 'Mention multi-plateformes',
      price: 2500,
      description: 'Campagne complète sur tous les réseaux',
    },
  ];

  const handleShootingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://readdy.ai/api/form/d44f9khha0trr1haa3mg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(shootingFormData).toString(),
      });

      if (response.ok) {
        setSubmitMessage('Merci ! Votre demande de shooting a été reçue.');
        setShootingFormData({
          nom: '',
          email: '',
          telephone: '',
          typeShoot: 'Standard',
          date: '',
          message: '',
        });
        setShowShootingForm(false);
      } else {
        setSubmitMessage('Erreur lors de l\'envoi. Veuillez réessayer.');
      }
    } catch (error) {
      setSubmitMessage('Erreur lors de l\'envoi. Veuillez réessayer.');
    }

    setIsSubmitting(false);
  };

  const handleCollabSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = {
        nom: collabFormData.nom,
        email: collabFormData.email,
        instagram: (collabFormData as any).instagram || '',
        followers: (collabFormData as any).followers || '',
        typeCollab: collabFormData.typeCollab,
        budget: collabFormData.budget,
        message: collabFormData.message,
      };

      const response = await fetch('https://readdy.ai/api/form/d44i8hemj2r470qadsfg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData).toString(),
      });

      if (response.ok) {
        setSubmitMessage('Merci ! Votre proposition de collaboration a été reçue.');
        setCollabFormData({
          nom: '',
          email: '',
          entreprise: '',
          secteur: '',
          typeCollab: 'Story Instagram',
          budget: '',
          message: '',
        });
        setShowCollabForm(false);
      } else {
        setSubmitMessage('Erreur lors de l\'envoi. Veuillez réessayer.');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setSubmitMessage('Erreur lors de l\'envoi. Veuillez réessayer.');
    }

    setIsSubmitting(false);
  };

  const handleSubscriptionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://readdy.ai/api/form/d44f9khha0trr1haa3n0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(subscriptionFormData).toString(),
      });

      if (response.ok) {
        setSubmitMessage('Merci ! Votre demande d\'abonnement a été reçue.');
        setSubscriptionFormData({
          nom: '',
          email: '',
          entreprise: '',
          secteur: '',
          pack: 'Pack Starter',
          message: '',
        });
        setShowSubscriptionForm(false);
      } else {
        setSubmitMessage('Erreur lors de l\'envoi. Veuillez réessayer.');
      }
    } catch (error) {
      setSubmitMessage('Erreur lors de l\'envoi. Veuillez réessayer.');
    }

    setIsSubmitting(false);
  };

  const handleBuyFormation = async (formation: any) => {
    setIsLoading(formation.name);
    
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-payment-optimized`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          productName: formation.name,
          price: formation.price,
          quantity: 1,
          productType: 'digital', // Produit numérique
          description: formation.description || 'Formation complète avec accès à vie + support + certificat + tutoriels vidéo',
        }),
      });

      const data = await response.json();
      
      if (data.url) {
        // Optimisation : préchargement de la page de succès
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = '/success';
        document.head.appendChild(link);
        
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Erreur lors de la création du paiement');
      }
    } catch (error) {
      console.error('Erreur:', error);
      
      // Affichage d'erreur plus user-friendly
      const errorMessage = error.message.includes('network') 
        ? 'Problème de connexion. Vérifiez votre internet et réessayez.'
        : 'Erreur lors du paiement. Veuillez réessayer dans quelques instants.';
        
      alert(errorMessage);
    } finally {
      setIsLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Floating 3D Elements Background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {/* Animated geometric shapes */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-gradient-to-br from-[#FF8A00]/30 to-[#FF6B00]/20 rounded-full animate-float-3d shadow-2xl shadow-[#FF8A00]/20"></div>
        <div className="absolute top-40 right-20 w-16 h-16 bg-gradient-to-br from-[#EAE7E2]/20 to-[#FF8A00]/10 rotate-45 animate-pulse-3d backdrop-blur-sm"></div>
        <div className="absolute bottom-40 left-20 w-24 h-24 border-2 border-[#FF8A00]/40 rounded-lg animate-spin-slow shadow-lg shadow-[#FF8A00]/30"></div>
        <div className="absolute top-60 right-10 w-12 h-12 bg-gradient-to-tr from-[#FF8A00]/40 to-transparent rounded-full animate-bounce-3d"></div>
        <div className="absolute bottom-20 right-40 w-18 h-18 border border-[#EAE7E2]/30 rotate-12 animate-float-reverse backdrop-blur-sm"></div>
        
        {/* Floating particles */}
        <div className="absolute top-32 left-1/3 w-2 h-2 bg-[#FF8A00] rounded-full animate-float-particle opacity-60"></div>
        <div className="absolute top-80 left-1/4 w-1 h-1 bg-[#EAE7E2] rounded-full animate-float-particle-slow opacity-40"></div>
        <div className="absolute bottom-60 right-1/3 w-3 h-3 bg-[#FF8A00]/60 rounded-full animate-pulse-particle"></div>
      </div>

      <Header />
      
      <div className="relative z-10 pt-20 pb-20">
        {/* Hero Section */}
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="transform hover:scale-105 transition-all duration-700 hover:rotate-1">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 animate-glow-text" style={{ fontFamily: 'Poppins, sans-serif' }}>
              VYBE PRODUCTION™
            </h1>
            <div className="w-32 h-1 bg-gradient-to-r from-[#FF8A00] to-[#FF6B00] mx-auto mb-8 animate-pulse-glow"></div>
            <p className="text-xl text-[#EAE7E2] max-w-4xl mx-auto leading-relaxed">
              VYBE PRODUCTION™ est l'agence de création et d'influence de VYBE™.<br />
              Nous regroupons photographes, vidéastes, mannequins et influenceurs (3 000 à 7 millions d'abonnés) pour booster l'image des marques et restaurants partenaires.
            </p>
          </div>
        </div>

        {/* Shootings Section */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-red-500/20 text-red-400 px-4 py-2 rounded-full text-sm font-semibold mb-4 animate-pulse">
                <i className="ri-fire-line"></i>
                PROMO LIMITÉE -30%
                <i className="ri-fire-line"></i>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Shootings <span className="text-orange-500">Photo & Vidéo</span>
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                Créez du contenu professionnel qui fait la différence
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  name: 'Portrait Pro',
                  originalPrice: 200,
                  price: 190,
                  duration: '1h',
                  description: 'Portraits professionnels pour vos réseaux',
                  image: 'https://static.readdy.ai/image/445bd39afde876450f502e12e8cf89e5/3c19786fd36f229ec5290f9c3fa94962.png',
                  includes: ['15 photos HD', 'Retouches incluses', '3 looks différents', 'Livraison 48h'],
                  popular: false,
                },
                {
                  name: 'Flying Dress',
                  originalPrice: 400,
                  price: 280,
                  duration: '1h30',
                  description: 'Shooting magique avec robe volante',
                  image: 'https://static.readdy.ai/image/445bd39afde876450f502e12e8cf89e5/6f858c5a3886ab390e2ba022ce24e84b.png',
                  includes: ['Robe fournie', '10 photos HD', 'Clip cinématique', 'Retouches premium'],
                  popular: true,
                },
                {
                  name: 'Lifestyle Content',
                  originalPrice: 350,
                  price: 390,
                  duration: '2h',
                  description: 'Contenu lifestyle pour vos collaborations',
                  image: 'https://static.readdy.ai/image/445bd39afde876450f502e12e8cf89e5/5a1f8f96b04fce457de27264e96110ec.png',
                  includes: ['25 photos HD', '5 vidéos courtes', 'Plusieurs décors', 'Usage commercial'],
                  popular: false,
                },
              ].map((shooting, index) => (
                <div
                  key={index}
                  className={`relative bg-gray-900 rounded-lg overflow-hidden hover:transform hover:scale-105 transition-all duration-300 ${
                    shooting.popular ? 'border-2 border-orange-500' : ''
                  }`}
                >
                  {/* Badge promo */}
                  <div className="absolute top-4 left-4 z-10 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">
                    -30% PROMO
                  </div>

                  {shooting.popular && (
                    <div className="bg-orange-500 text-black text-sm font-bold px-4 py-2 text-center">
                      LE PLUS POPULAIRE
                    </div>
                  )}

                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={shooting.image}
                      alt={shooting.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-white mb-1">{shooting.name}</h3>
                        <p className="text-gray-400 text-sm">{shooting.duration}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg text-gray-500 line-through">{shooting.originalPrice}€</p>
                        <p className="text-2xl font-bold text-orange-500">{shooting.price}€</p>
                      </div>
                    </div>

                    <p className="text-gray-300 mb-6">{shooting.description}</p>

                    <div className="mb-6">
                      <h4 className="text-white font-semibold mb-3">Inclus :</h4>
                      <ul className="space-y-2">
                        {shooting.includes.map((item, idx) => (
                          <li key={idx} className="flex items-start text-gray-300 text-sm">
                            <i className="ri-check-line text-orange-500 mr-2 mt-0.5 flex-shrink-0"></i>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <button
                      onClick={() => handleBooking('shooting', shooting.name, shooting.price)}
                      className="w-full py-3 bg-orange-500 text-black font-semibold rounded-lg hover:bg-orange-600 transition-colors flex items-center justify-center gap-2"
                    >
                      <i className="ri-calendar-line"></i>
                      Réserver maintenant
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Formations Section */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-green-500/20 text-green-400 px-4 py-2 rounded-full text-sm font-semibold mb-4 animate-pulse">
                <i className="ri-discount-percent-line"></i>
                OFFRE SPÉCIALE -50%
                <i className="ri-discount-percent-line"></i>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                <span className="text-orange-500">Formation</span> Premium
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                La méthode complète pour réussir dans l'influence
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden border border-orange-500/30">
                {/* Badge promo principal */}
                <div className="absolute top-6 right-6 z-10 bg-green-500 text-white text-sm font-bold px-4 py-2 rounded-full animate-bounce">
                  -50% AUJOURD'HUI
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
                  <div>
                    <div className="mb-6">
                      <h3 className="text-3xl font-bold text-white mb-2">
                        SECRETS D'INFLUENCE MILLIONNAIRE
                      </h3>
                      <p className="text-orange-400 font-semibold mb-4">
                        La méthode EXACTE pour passer de 0 à 100K€/an avec l'influence
                      </p>
                      <div className="flex items-center gap-4 mb-4">
                        <div className="text-right">
                          <p className="text-2xl text-gray-500 line-through">394€</p>
                          <p className="text-4xl font-bold text-green-400">197€</p>
                        </div>
                        <div className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                          ÉCONOMIE 197€
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4 mb-8">
                      {[
                        '🎯 Stratégie de croissance explosive (0 à 100K abonnés)',
                        '💰 Techniques de monétisation secrètes',
                        '🤝 Négociation avec les marques (tarifs x3)',
                        '📈 Algorithmes décryptés (viralité garantie)',
                        '🎬 Création de contenu qui convertit',
                        '💎 Accès VIP communauté privée',
                        '🎁 BONUS: Templates contrats + Media Kit',
                      ].map((feature, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <i className="ri-check-double-line text-green-400 mt-1 flex-shrink-0"></i>
                          <span className="text-gray-300 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-6">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-time-line text-orange-500"></i>
                        <span className="text-orange-400 font-semibold text-sm">OFFRE LIMITÉE</span>
                      </div>
                      <p className="text-gray-300 text-sm">
                        Plus que <span className="text-orange-500 font-bold">48H</span> pour profiter de cette réduction exceptionnelle !
                      </p>
                    </div>

                    <button
                      onClick={() => handleBooking('formation', 'Secrets d\'Influence Millionnaire', 197)}
                      className="w-full py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 text-lg"
                    >
                      <i className="ri-rocket-line"></i>
                      ACCÉDER MAINTENANT - 197€
                    </button>

                    <div className="flex items-center justify-center gap-4 mt-4 text-sm text-gray-400">
                      <div className="flex items-center gap-1">
                        <i className="ri-shield-check-line text-green-400"></i>
                        Garantie 30 jours
                      </div>
                      <div className="flex items-center gap-1">
                        <i className="ri-star-fill text-yellow-400"></i>
                        4.9/5 (847 avis)
                      </div>
                    </div>
                  </div>

                  <div className="relative">
                    <img
                      src="https://public.readdy.ai/ai/img_res/785359be59bb1900399e554e50a10458.jpg"
                      alt="Formation Success"
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent rounded-lg"></div>
                    
                    {/* Témoignages flottants */}
                    <div className="absolute bottom-4 left-4 right-4 space-y-2">
                      <div className="bg-black/80 backdrop-blur-sm rounded-lg p-3 text-xs">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <i key={i} className="ri-star-fill text-yellow-400 text-xs"></i>
                            ))}
                          </div>
                          <span className="text-white font-semibold">Sarah M.</span>
                        </div>
                        <p className="text-gray-300">"J'ai multiplié mes revenus par 5 en 3 mois !"</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Presets Section */}
        <section className="py-20 px-4 bg-gradient-to-br from-purple-900 via-black to-pink-900 relative overflow-hidden">
          {/* Animated Background */}
          <div className="absolute inset-0">
            <div className="absolute top-20 left-10 w-32 h-32 bg-purple-500/20 rounded-full blur-xl animate-pulse"></div>
            <div className="absolute bottom-20 right-10 w-40 h-40 bg-pink-500/20 rounded-full blur-xl animate-pulse delay-1000"></div>
            <div className="absolute top-1/2 left-1/2 w-60 h-60 bg-orange-500/10 rounded-full blur-2xl animate-pulse delay-500"></div>
          </div>

          <div className="max-w-6xl mx-auto relative z-10">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-full text-sm font-bold mb-6 animate-pulse">
                <i className="ri-smartphone-line"></i>
                COMPATIBLE MOBILE • UTILISÉ PAR LES STARS
              </div>
              
              <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
                <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 bg-clip-text text-transparent">
                  PRESETS MOBILE
                </span>
                <br />
                <span className="text-white">DES STARS</span>
              </h2>
              
              <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                🌟 <strong>les MÊMES PRESETS utilisés par Kylie Jenner, Bella Hadid, Hailey Bieber</strong> 🌟
                <br />
                Transformez vos photos iPhone en contenus VIRAUX avec nos presets mobiles exclusifs !
              </p>

              {/* Stars Using Presets */}
              <div className="flex justify-center items-center gap-8 mb-12 flex-wrap">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center mb-2 mx-auto">
                    <i className="ri-star-fill text-white text-2xl"></i>
                  </div>
                  <p className="text-white font-semibold">Kylie Jenner</p>
                  <p className="text-gray-400 text-sm">400M followers</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mb-2 mx-auto">
                    <i className="ri-star-fill text-white text-2xl"></i>
                  </div>
                  <p className="text-white font-semibold">Bella Hadid</p>
                  <p className="text-gray-400 text-sm">60M followers</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-pink-500 rounded-full flex items-center justify-center mb-2 mx-auto">
                    <i className="ri-star-fill text-white text-2xl"></i>
                  </div>
                  <p className="text-white font-semibold">Hailey Bieber</p>
                  <p className="text-gray-400 text-sm">50M followers</p>
                </div>
              </div>
            </div>

            {/* Main Preset Package */}
            <div className="relative">
              {/* Floating Particles */}
              <div className="absolute -top-10 -left-10 w-20 h-20 bg-purple-500/30 rounded-full blur-xl animate-bounce"></div>
              <div className="absolute -bottom-10 -right-10 w-24 h-24 bg-pink-500/30 rounded-full blur-xl animate-bounce delay-1000"></div>
              
              <div className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl rounded-3xl p-8 border border-purple-500/30 hover:border-purple-500/60 transition-all duration-500 hover:transform hover:scale-105 relative overflow-hidden">
                {/* Promo Badge */}
                <div className="absolute -top-3 -right-3 bg-gradient-to-r from-red-500 to-pink-500 text-white px-6 py-2 rounded-full text-sm font-bold transform rotate-12 animate-pulse shadow-lg">
                  MÉGA PROMO -84%
                </div>

                {/* Urgency Timer */}
                <div className="absolute top-4 left-4 bg-red-500/90 text-white px-4 py-2 rounded-full text-sm font-bold animate-pulse">
                  ⏰ 48H SEULEMENT
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  {/* Left: Before/After with 3D Animation */}
                  <div className="relative">
                    <h3 className="text-2xl font-bold text-white mb-6 text-center">
                      TRANSFORMATION MAGIQUE 📱✨
                    </h3>
                    
                    <div className="relative group cursor-pointer">
                      {/* Before/After Container */}
                      <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                        <div className="grid grid-cols-2 gap-1">
                          {/* Before */}
                          <div className="relative group-hover:transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-700">
                            <img
                              src="https://readdy.ai/api/search-image?query=Original%20iPhone%20photo%20before%20preset%2C%20natural%20lighting%2C%20unedited%20mobile%20photography%2C%20casual%20selfie%2C%20normal%20colors&width=200&height=300&seq=mobile-before&orientation=portrait"
                              alt="Avant"
                              className="w-full aspect-[3/4] object-cover"
                            />
                            <div className="absolute inset-0 bg-gray-500/20 flex items-center justify-center">
                              <span className="bg-black/70 text-white px-3 py-1 rounded-full text-sm font-bold">AVANT</span>
                            </div>
                          </div>
                          
                          {/* After */}
                          <div className="relative group-hover:transform group-hover:scale-110 group-hover:-rotate-3 transition-all duration-700">
                            <img
                              src="https://readdy.ai/api/search-image?query=After%20mobile%20preset%20transformation%2C%20Instagram-worthy%20photo%2C%20professional%20mobile%20photography%2C%20enhanced%20colors%2C%20influencer%20aesthetic%2C%20viral%20content&width=200&height=300&seq=mobile-after&orientation=portrait"
                              alt="Après"
                              className="w-full aspect-[3/4] object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-purple-500/30 to-transparent flex items-center justify-center">
                              <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-sm font-bold">APRÈS ✨</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Magic Effect Overlay */}
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                            <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center animate-spin">
                              <i className="ri-magic-line text-white text-2xl"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Floating Elements */}
                      <div className="absolute -top-4 -left-4 w-8 h-8 bg-yellow-400 rounded-full animate-bounce opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        ✨
                      </div>
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-pink-400 rounded-full animate-bounce delay-300 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        💫
                      </div>
                      <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-purple-400 rounded-full animate-bounce delay-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        🌟
                      </div>
                    </div>

                    {/* Mobile Apps Compatibility */}
                    <div className="mt-6 text-center">
                      <p className="text-white font-semibold mb-3">📱 Compatible avec :</p>
                      <div className="flex justify-center gap-4 flex-wrap">
                        <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full text-sm">Lightroom Mobile</span>
                        <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-1 rounded-full text-sm">VSCO</span>
                        <span className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-3 py-1 rounded-full text-sm">Snapseed</span>
                      </div>
                    </div>
                  </div>

                  {/* Right: Package Details */}
                  <div>
                    <div className="text-center mb-8">
                      <h3 className="text-3xl font-bold text-white mb-2">
                        PACK ULTIMATE MOBILE
                      </h3>
                      <p className="text-purple-400 font-semibold mb-4">
                        🏆 #1 MONDIAL • UTILISÉ PAR +2M CRÉATEURS
                      </p>
                      
                      {/* Price */}
                      <div className="flex items-center justify-center gap-4 mb-6">
                        <span className="text-3xl text-gray-500 line-through">595€</span>
                        <span className="text-5xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">97€</span>
                      </div>
                      
                      <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-4 py-2 rounded-full text-sm font-bold inline-block mb-6">
                        ÉCONOMIE DE 498€ (-84%)
                      </div>
                    </div>

                    {/* What's Included */}
                    <div className="space-y-4 mb-8">
                      <h4 className="text-xl font-bold text-white mb-4">🎁 PACK COMPLET INCLUS :</h4>
                      
                      <div className="grid grid-cols-1 gap-3">
                        <div className="flex items-center text-white">
                          <i className="ri-smartphone-line text-purple-400 mr-3 text-xl"></i>
                          <span><strong>50+ Presets Mobile</strong> (iPhone & Android)</span>
                        </div>
                        <div className="flex items-center text-white">
                          <i className="ri-star-fill text-yellow-400 mr-3 text-xl"></i>
                          <span><strong>Presets des Stars</strong> (Kylie, Bella, Hailey...)</span>
                        </div>
                        <div className="flex items-center text-white">
                          <i className="ri-video-line text-pink-400 mr-3 text-xl"></i>
                          <span><strong>Tutoriels Vidéo</strong> installation mobile</span>
                        </div>
                        <div className="flex items-center text-white">
                          <i className="ri-download-line text-green-400 mr-3 text-xl"></i>
                          <span><strong>Installation 1-Click</strong> sur mobile</span>
                        </div>
                        <div className="flex items-center text-white">
                          <i className="ri-refresh-line text-blue-400 mr-3 text-xl"></i>
                          <span><strong>Mises à jour à VIE</strong> gratuites</span>
                        </div>
                        <div className="flex items-center text-white">
                          <i className="ri-shield-check-line text-orange-400 mr-3 text-xl"></i>
                          <span><strong>Garantie 30 jours</strong> satisfait ou remboursé</span>
                        </div>
                      </div>
                    </div>

                    {/* Social Proof */}
                    <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 rounded-xl p-4 mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                          ))}
                        </div>
                        <span className="text-white font-bold">4.9/5</span>
                      </div>
                      <p className="text-gray-300 text-sm italic">
                        "Mes photos iPhone ressemblent maintenant à celles des magazines ! 
                        J'ai gagné 50K followers en 2 mois !" - Sarah M.
                      </p>
                      <p className="text-purple-400 text-xs mt-2">
                        ⭐ 10,247 créateurs satisfaits
                      </p>
                    </div>

                    {/* CTA Buttons - OPTIMISÉ */}
                    <div className="space-y-4">
                      <button
                        onClick={() => handleBuyNow({ id: 'presets-mobile', name: 'Pack Ultimate Mobile', price: 97 })}
                        disabled={isLoading === 'presets-mobile'}
                        className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 text-lg shadow-lg hover:shadow-purple-500/25"
                      >
                        {isLoading === 'presets-mobile' ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Création du paiement...</span>
                          </>
                        ) : (
                          <>
                            <i className="ri-smartphone-line text-xl"></i>
                            <span>TÉLÉCHARGER MAINTENANT - 97€</span>
                            <i className="ri-arrow-right-line text-xl"></i>
                          </>
                        )}
                      </button>

                      <div className="text-center">
                        <p className="text-gray-400 text-sm">
                          💳 Paiement sécurisé Stripe • 📱 Téléchargement immédiat • 🔒 Garantie 30j
                        </p>
                        <p className="text-gray-500 text-xs mt-2">
                          ✅ Produit numérique - Pas de frais de livraison • Email avec liens de téléchargement + tutoriels
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom Features */}
                <div className="mt-12 pt-8 border-t border-purple-500/30">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                    <div>
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-2">
                        <i className="ri-fire-line text-white text-xl"></i>
                      </div>
                      <p className="text-white font-semibold text-sm">VIRAL READY</p>
                      <p className="text-gray-400 text-xs">Photos Instagram</p>
                    </div>
                    <div>
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-2">
                        <i className="ri-smartphone-line text-white text-xl"></i>
                      </div>
                      <p className="text-white font-semibold text-sm">100% MOBILE</p>
                      <p className="text-gray-400 text-xs">iPhone & Android</p>
                    </div>
                    <div>
                      <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-2">
                        <i className="ri-magic-line text-white text-xl"></i>
                      </div>
                      <p className="text-white font-semibold text-sm">1-CLICK</p>
                      <p className="text-gray-400 text-xs">Installation facile</p>
                    </div>
                    <div>
                      <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-2">
                        <i className="ri-star-fill text-white text-xl"></i>
                      </div>
                      <p className="text-white font-semibold text-sm">PRO LEVEL</p>
                      <p className="text-gray-400 text-xs">Qualité studio</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="text-center mt-12">
              <p className="text-2xl font-bold text-white mb-4">
                🚀 Rejoignez les <span className="text-purple-400">2M+ créateurs</span> qui utilisent nos presets !
              </p>
              <p className="text-gray-300 mb-6">
                Transformez votre iPhone en studio photo professionnel en 1 clic
              </p>
              
              <div className="flex justify-center gap-8 text-center">
                <div>
                  <p className="text-3xl font-bold text-purple-400">2M+</p>
                  <p className="text-gray-400 text-sm">Téléchargements</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-pink-400">4.9★</p>
                  <p className="text-gray-400 text-sm">Note moyenne</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-orange-400">50+</p>
                  <p className="text-gray-400 text-sm">Presets inclus</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section Candidature Créateurs de Contenu - NOUVELLE SECTION */}
        <section className="py-20 bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 relative overflow-hidden">
          {/* Effets de fond animés */}
          <div className="absolute inset-0">
            <div className="absolute top-10 left-10 w-32 h-32 bg-orange-200/30 rounded-full blur-xl animate-pulse"></div>
            <div className="absolute bottom-20 right-20 w-40 h-40 bg-red-200/30 rounded-full blur-xl animate-pulse delay-1000"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-pink-200/20 rounded-full blur-2xl animate-pulse delay-500"></div>
          </div>

          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-6xl mx-auto">
              {/* Header */}
              <div className="text-center mb-16">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-600 to-red-600 text-white px-6 py-3 rounded-full text-sm font-bold mb-6 animate-pulse">
                  <i className="ri-star-fill"></i>
                  <span>REJOINS L'AGENCE VYBE</span>
                  <i className="ri-fire-fill"></i>
                </div>
                
                <h2 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 bg-clip-text text-transparent mb-6">
                  CRÉATEURS DE CONTENU
                </h2>
                
                <p className="text-2xl text-gray-700 mb-4 font-semibold">
                  Influenceurs • Mannequins • Artistes • Talents UGC
                </p>
                
                <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-8 py-4 rounded-2xl inline-block mb-8 shadow-2xl">
                  <p className="text-xl font-bold">
                    🔥 SEULEMENT 1000 PLACES DISPONIBLES 🔥
                  </p>
                  <p className="text-sm opacity-90">
                    Après 1000 inscriptions → Liste d'attente
                  </p>
                </div>

                <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-4xl mx-auto">
                  Rejoins l'agence VYBE et travaille avec les plus grandes marques ! 
                  <br />
                  <span className="font-semibold text-orange-600">Si notre équipe valide ton profil, tu seras admis(e) dans l'agence</span>
                </p>
              </div>

              {/* Statistiques et Avantages */}
              <div className="grid md:grid-cols-4 gap-6 mb-16">
                <div className="text-center p-6 bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/90 transition-all duration-300 transform hover:scale-105 shadow-lg">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <i className="ri-money-euro-circle-fill text-2xl text-white"></i>
                  </div>
                  <h4 className="font-bold text-gray-800 mb-2">Revenus Élevés</h4>
                  <p className="text-gray-600 text-sm">Jusqu'à 5000€/mois</p>
                </div>
                
                <div className="text-center p-6 bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/90 transition-all duration-300 transform hover:scale-105 shadow-lg">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <i className="ri-building-fill text-2xl text-white"></i>
                  </div>
                  <h4 className="font-bold text-gray-800 mb-2">Grandes Marques</h4>
                  <p className="text-gray-600 text-sm">Nike, Dior, Mercedes...</p>
                </div>
                
                <div className="text-center p-6 bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/90 transition-all duration-300 transform hover:scale-105 shadow-lg">
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <i className="ri-rocket-fill text-2xl text-white"></i>
                  </div>
                  <h4 className="font-bold text-gray-800 mb-2">Croissance Rapide</h4>
                  <p className="text-gray-600 text-sm">+500% d'audience</p>
                </div>
                
                <div className="text-center p-6 bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/90 transition-all duration-300 transform hover:scale-105 shadow-lg">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <i className="ri-shield-check-fill text-2xl text-white"></i>
                  </div>
                  <h4 className="font-bold text-gray-800 mb-2">Support 24/7</h4>
                  <p className="text-gray-600 text-sm">Équipe dédiée</p>
                </div>
              </div>

              {/* Formulaire de candidature */}
              <div className="bg-white/90 backdrop-blur-lg rounded-3xl p-8 md:p-12 shadow-2xl border border-white/20 relative overflow-hidden group hover:shadow-3xl transition-all duration-500 transform hover:scale-[1.02]">
                {/* Effet brillant animé */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                
                <div className="text-center mb-8">
                  <h3 className="text-3xl font-bold text-gray-800 mb-4">
                    🎯 CANDIDATURE AGENCE VYBE
                  </h3>
                  <p className="text-lg text-gray-600">
                    Dis-nous ce que tu peux <span className="font-bold text-orange-600">RÉELLEMENT</span> nous rapporter
                  </p>
                </div>

                <form 
                  data-readdy-form
                  action="https://readdy.ai/api/form/d46i6k2hchb23d4efaug"
                  method="POST"
                  className="space-y-6 relative z-10"
                >
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Informations personnelles */}
                    <div className="space-y-6">
                      <h4 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <i className="ri-user-3-fill text-orange-600"></i>
                        Informations personnelles
                      </h4>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Nom complet *</label>
                        <input
                          type="text"
                          name="nom_complet"
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 bg-white/70"
                          placeholder="Ton nom et prénom"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 bg-white/70"
                          placeholder="ton@email.com"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone *</label>
                        <input
                          type="tel"
                          name="telephone"
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 bg-white/70"
                          placeholder="06 12 34 56 78"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Âge</label>
                        <input
                          type="number"
                          name="age"
                          min="16"
                          max="99"
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 bg-white/70"
                          placeholder="25"
                        />
                      </div>
                    </div>

                    {/* Profil créateur */}
                    <div className="space-y-6">
                      <h4 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <i className="ri-camera-fill text-red-600"></i>
                        Ton profil créateur
                      </h4>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Type de créateur *</label>
                        <div className="space-y-2">
                          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl hover:bg-orange-50 transition-colors cursor-pointer">
                            <input type="checkbox" name="type_createur" value="Influenceur" className="text-orange-600 focus:ring-orange-500" />
                            <span>📱 Influenceur</span>
                          </label>
                          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl hover:bg-orange-50 transition-colors cursor-pointer">
                            <input type="checkbox" name="type_createur" value="Mannequin" className="text-orange-600 focus:ring-orange-500" />
                            <span>👗 Mannequin</span>
                          </label>
                          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl hover:bg-orange-50 transition-colors cursor-pointer">
                            <input type="checkbox" name="type_createur" value="Artiste" className="text-orange-600 focus:ring-orange-500" />
                            <span>🎨 Artiste</span>
                          </label>
                          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl hover:bg-orange-50 transition-colors cursor-pointer">
                            <input type="checkbox" name="type_createur" value="Créateur UGC" className="text-orange-600 focus:ring-orange-500" />
                            <span>🎬 Créateur UGC</span>
                          </label>
                          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl hover:bg-orange-50 transition-colors cursor-pointer">
                            <input type="checkbox" name="type_createur" value="Talent" className="text-orange-600 focus:ring-orange-500" />
                            <span>⭐ Talent</span>
                          </label>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Instagram *</label>
                        <input
                          type="text"
                          name="instagram"
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 bg-white/70"
                          placeholder="@toncompte"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">TikTok</label>
                        <input
                          type="text"
                          name="tiktok"
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 bg-white/70"
                          placeholder="@toncompte"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Nombre d'abonnés Instagram *</label>
                        <select
                          name="abonnes_instagram"
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 bg-white/70"
                        >
                          <option value="">Sélectionne ta tranche</option>
                          <option value="1K-10K">1K - 10K</option>
                          <option value="10K-50K">10K - 50K</option>
                          <option value="50K-100K">50K - 100K</option>
                          <option value="100K-500K">100K - 500K</option>
                          <option value="500K+">500K+</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Ce que tu peux apporter */}
                  <div className="space-y-6 pt-6 border-t border-gray-200">
                    <h4 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <i className="ri-lightbulb-fill text-pink-600"></i>
                      Ce que tu peux RÉELLEMENT nous rapporter
                    </h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tes compétences uniques *</label>
                      <textarea
                        name="competences_uniques"
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 bg-white/70 resize-none"
                        placeholder="Qu'est-ce qui te rend unique ? Tes talents spéciaux, ton style, ton expertise... (max 500 caractères)"
                      ></textarea>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Valeur ajoutée pour VYBE *</label>
                      <textarea
                        name="valeur_ajoutee"
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 bg-white/70 resize-none"
                        placeholder="Comment peux-tu faire gagner de l'argent à VYBE ? Tes connexions, ton audience, tes projets... (max 500 caractères)"
                      ></textarea>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Expérience avec les marques</label>
                      <textarea
                        name="experience_marques"
                        rows={3}
                        maxLength={500}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 bg-white/70 resize-none"
                        placeholder="Tes collaborations passées, tes réussites, tes tarifs habituels... (max 500 caractères)"
                      ></textarea>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Objectifs de revenus</label>
                      <select
                        name="objectifs_revenus"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 bg-white/70"
                      >
                        <option value="">Sélectionne ton objectif</option>
                        <option value="500-1000€/mois">500 - 1000€/mois</option>
                        <option value="1000-2500€/mois">1000 - 2500€/mois</option>
                        <option value="2500-5000€/mois">2500 - 5000€/mois</option>
                        <option value="5000€+/mois">5000€+/mois</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Disponibilité</label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <label className="flex items-center gap-2 p-3 border border-gray-200 rounded-xl hover:bg-orange-50 transition-colors cursor-pointer">
                          <input type="checkbox" name="disponibilite" value="Immédiate" className="text-orange-600 focus:ring-orange-500" />
                          <span className="text-sm">Immédiate</span>
                        </label>
                        <label className="flex items-center gap-2 p-3 border border-gray-200 rounded-xl hover:bg-orange-50 transition-colors cursor-pointer">
                          <input type="checkbox" name="disponibilite" value="Week-end" className="text-orange-600 focus:ring-orange-500" />
                          <span className="text-sm">Week-end</span>
                        </label>
                        <label className="flex items-center gap-2 p-3 border border-gray-200 rounded-xl hover:bg-orange-50 transition-colors cursor-pointer">
                          <input type="checkbox" name="disponibilite" value="Semaine" className="text-orange-600 focus:ring-orange-500" />
                          <span className="text-sm">Semaine</span>
                        </label>
                        <label className="flex items-center gap-2 p-3 border border-gray-200 rounded-xl hover:bg-orange-50 transition-colors cursor-pointer">
                          <input type="checkbox" name="disponibilite" value="Flexible" className="text-orange-600 focus:ring-orange-500" />
                          <span className="text-sm">Flexible</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Bouton de soumission */}
                  <div className="pt-8 text-center">
                    <button
                      type="submit"
                      className="group relative inline-flex items-center gap-3 bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 text-white px-12 py-4 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-orange-500/25 transition-all duration-500 transform hover:scale-105 hover:-translate-y-1 overflow-hidden"
                    >
                      {/* Effet brillant */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                      
                      <i className="ri-send-plane-fill text-xl group-hover:rotate-12 transition-transform duration-300"></i>
                      <span>POSTULER MAINTENANT</span>
                      
                      {/* Particules animées */}
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-ping"></div>
                      <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-pink-400 rounded-full animate-ping delay-300"></div>
                    </button>
                    
                    <div className="mt-6 space-y-2">
                      <p className="text-sm text-gray-600 flex items-center justify-center gap-2">
                        <i className="ri-time-fill text-green-500"></i>
                        <span>Validation en 24-48h • Si accepté, contrat envoyé par email</span>
                      </p>
                      <p className="text-xs text-gray-500">
                        ⚠️ Après 1000 inscriptions, les candidatures seront mises en liste d'attente
                      </p>
                    </div>
                  </div>
                </form>
              </div>

              {/* Processus de sélection */}
              <div className="mt-16 bg-gradient-to-r from-gray-900 to-black rounded-3xl p-8 text-white">
                <h3 className="text-2xl font-bold text-center mb-8">
                  🎯 PROCESSUS DE SÉLECTION VYBE
                </h3>
                
                <div className="grid md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold">1</span>
                    </div>
                    <h4 className="font-bold mb-2">Candidature</h4>
                    <p className="text-gray-300 text-sm">Tu remplis le formulaire avec tes infos</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold">2</span>
                    </div>
                    <h4 className="font-bold mb-2">Analyse</h4>
                    <p className="text-gray-300 text-sm">Notre équipe étudie ton profil</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold">3</span>
                    </div>
                    <h4 className="font-bold mb-2">Validation</h4>
                    <p className="text-gray-300 text-sm">Réponse en 24-48h maximum</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold">4</span>
                    </div>
                    <h4 className="font-bold mb-2">Contrat</h4>
                    <p className="text-gray-300 text-sm">Si accepté, contrat par email</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Abonnements Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16 transform hover:scale-105 transition-all duration-500">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-full text-sm font-bold mb-6 animate-pulse">
                <i className="ri-restaurant-line"></i>
                ABONNEMENT MENSUEL • MARQUES & RESTAURANTS
                <i className="ri-store-line"></i>
              </div>
              <h2 className="text-4xl font-bold text-white mb-6 animate-slide-up" style={{ fontFamily: 'Poppins, sans-serif' }}>
                🔸 Abonnements <span className="text-orange-500">Marques / Restaurants</span>
              </h2>
              
              {/* Concept Explanation */}
              <div className="max-w-4xl mx-auto mb-12">
                <div className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl rounded-2xl p-8 border border-orange-500/30 animate-glow-border">
                  <h3 className="text-2xl font-bold text-orange-500 mb-4">💡 Le Concept VYBE Production</h3>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    <strong className="text-white">Abonnement mensuel tout inclus :</strong> Votre marque ou restaurant bénéficie chaque semaine de la visite de nos créateurs de contenu sélectionnés pour leur <span className="text-orange-500 font-semibold">engagement exceptionnel</span> et leur audience qualifiée.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 text-center">
                      <i className="ri-group-line text-orange-500 text-3xl mb-2"></i>
                      <h4 className="text-white font-semibold mb-2">Créateurs Sélectionnés</h4>
                      <p className="text-gray-300 text-sm">Influenceurs avec engagement authentique et audience active</p>
                    </div>
                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 text-center">
                      <i className="ri-calendar-check-line text-orange-500 text-3xl mb-2"></i>
                      <h4 className="text-white font-semibold mb-2">Visites Régulières</h4>
                      <p className="text-gray-300 text-sm">Contenu frais chaque semaine pour maintenir la visibilité</p>
                    </div>
                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 text-center">
                      <i className="ri-rocket-line text-orange-500 text-3xl mb-2"></i>
                      <h4 className="text-white font-semibold mb-2">Promotion Continue</h4>
                      <p className="text-gray-300 text-sm">Stories, posts et vidéos pour booster votre notoriété</p>
                    </div>
                  </div>
                  
                  <div className="bg-green-500/20 border border-green-500 rounded-lg p-4">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <i className="ri-check-double-line text-green-400 text-xl"></i>
                      <span className="text-green-400 font-bold">RÉSULTAT GARANTI</span>
                    </div>
                    <p className="text-green-300 text-center">
                      Augmentation mesurable de votre visibilité et de votre clientèle grâce à nos créateurs à fort engagement
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  name: 'Pack Découverte',
                  price: 190,
                  originalPrice: 290,
                  period: '/mois',
                  description: '3-4 micro influenceurs (5K → 25K) + stories et posts hebdomadaires',
                  features: [
                    '3-4 micro influenceurs qualifiés',
                    '5K à 25K abonnés engagés',
                    '2 visites par semaine minimum',
                    'Stories + posts garantis',
                    'Rapport mensuel détaillé',
                    'Contenu photo professionnel'
                  ],
                  popular: false,
                  badge: 'IDÉAL DÉBUTANT',
                  badgeColor: 'from-blue-500 to-cyan-500'
                },
                {
                  name: 'Pack Business',
                  price: 390,
                  originalPrice: 590,
                  period: '/mois',
                  description: '6-8 influenceurs + 2 reels/TikToks + shooting photo inclus',
                  features: [
                    '6-8 influenceurs sélectionnés',
                    '10K à 100K abonnés',
                    '3 visites par semaine',
                    '2 Reels ou TikToks mensuels',
                    'Shooting photo professionnel',
                    'Gestion réseaux sociaux',
                    'Analytics et ROI tracking',
                    '🔥 EXCLUSIF : Shooting mensuel avec Adel Saï (@Adofficielo)',
                    '📱 5.4M TikTok • 2.6M Instagram • 1.6M Facebook',
                    '🎬 Reel + TikTok avec mention collab garantis'
                  ],
                  popular: true,
                  badge: 'MEILLEURE OFFRE + ADEL SAÏ',
                  badgeColor: 'from-orange-500 to-red-500'
                },
                {
                  name: 'Pack Premium',
                  price: 790,
                  originalPrice: 1190,
                  period: '/mois',
                  description: '12-15 influenceurs jusqu\'à 500K + vidéos montées + événements',
                  features: [
                    '12-15 influenceurs premium',
                    'Jusqu\'à 500K abonnés',
                    '4-5 visites par semaine',
                    'Vidéos montées professionnelles',
                    'Organisation d\'événements',
                    'Collaborations avec célébrités',
                    'Stratégie marketing complète',
                    'Support dédié 7j/7'
                  ],
                  popular: false,
                  badge: 'CROISSANCE RAPIDE',
                  badgeColor: 'from-purple-500 to-pink-500'
                },
                {
                  name: 'Pack Élite',
                  price: 1590,
                  originalPrice: 2390,
                  period: '/mois',
                  description: 'Macro influenceurs (500K → 5M), direction artistique et équipe Community Manager dédiée',
                  features: [
                    'Macro influenceurs 500K-5M',
                    'Célébrités et stars confirmées',
                    'Visites quotidiennes possibles',
                    'Direction artistique complète',
                    'Équipe Community Manager dédiée',
                    'Gestion complète réseaux sociaux',
                    'Campagnes virales sur mesure',
                    'Couverture médiatique',
                    'Partenariats exclusifs',
                    'Équipe dédiée 24h/24',
                    'Garantie résultats'
                  ],
                  popular: false,
                  badge: 'PRESTIGE MAXIMUM',
                  badgeColor: 'from-yellow-500 to-orange-500'
                }
              ].map((pack, index) => (
                <div 
                  key={pack.name} 
                  className={`relative bg-gradient-to-br from-[#1A1A1A] to-[#0F0F0F] rounded-2xl p-8 border transition-all duration-700 hover:scale-110 hover:rotate-2 transform-gpu animate-card-float ${
                    pack.popular 
                      ? 'border-[#FF8A00] shadow-2xl shadow-[#FF8A00]/30 animate-glow-border' 
                      : 'border-[#333] hover:border-[#FF8A00]/50'
                  }`}
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  {/* Promo Badge */}
                  <div className="absolute -top-3 -right-3 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">
                    -35% PROMO
                  </div>

                  {pack.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-[#FF8A00] to-[#FF6B00] text-white px-6 py-2 rounded-full text-sm font-bold animate-pulse-glow">
                      {pack.badge}
                    </div>
                  )}

                  {!pack.popular && (
                    <div className={`absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r ${pack.badgeColor} text-white px-4 py-2 rounded-full text-xs font-bold`}>
                      {pack.badge}
                    </div>
                  )}

                  {/* Special badge for Pack Business with Adel Saï */}
                  {pack.name === 'Pack Business' && (
                    <div className="absolute top-4 left-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-black px-3 py-1 rounded-full text-xs font-bold animate-pulse">
                      + ADEL SAÏ EXCLUSIF
                    </div>
                  )}

                  {/* Special badge for Pack Élite */}
                  {pack.name === 'Pack Élite' && (
                    <div className="absolute top-4 left-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse">
                      + COMMUNITY MANAGER
                    </div>
                  )}
                  
                  <div className="text-center mb-6 mt-4">
                    <h3 className="text-2xl font-bold text-white mb-2">{pack.name}</h3>
                    <div className="flex items-baseline justify-center mb-4">
                      <span className="text-lg text-gray-500 line-through mr-2">{pack.originalPrice}€</span>
                      <span className="text-4xl font-bold text-[#FF8A00] animate-price-glow">{pack.price}€</span>
                      <span className="text-[#EAE7E2] ml-2">{pack.period}</span>
                    </div>
                    <div className="bg-green-500/20 border border-green-500 rounded-lg px-3 py-1 text-green-400 text-sm font-bold inline-block mb-4">
                      ÉCONOMIE {pack.originalPrice - pack.price}€
                    </div>
                    <p className="text-[#EAE7E2] text-sm leading-relaxed">{pack.description}</p>
                  </div>
                  
                  <ul className="space-y-3 mb-8">
                    {pack.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-[#EAE7E2] text-sm">
                        <i className="ri-check-line text-[#FF8A00] mr-3 text-lg animate-bounce-check flex-shrink-0 mt-0.5"></i>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button 
                    onClick={() => setShowSubscriptionForm(true)}
                    className="w-full bg-gradient-to-r from-[#FF8A00] to-[#FF6B00] text-white py-3 rounded-xl font-bold transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-[#FF8A00]/50 transform-gpu animate-button-glow"
                  >
                    Démarrer l'abonnement
                  </button>

                  {/* ROI Indicator */}
                  <div className="mt-4 bg-blue-500/20 border border-blue-500/30 rounded-lg p-3 text-center">
                    <div className="text-blue-400 font-semibold text-sm">ROI Estimé</div>
                    <div className="text-blue-300 text-xs">
                      {pack.name === 'Pack Découverte' && '+150% de visibilité'}
                      {pack.name === 'Pack Business' && '+300% de clientèle'}
                      {pack.name === 'Pack Premium' && '+500% de notoriété'}
                      {pack.name === 'Pack Élite' && '+1000% d\'impact'}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Success Stories */}
            <div className="mt-16 bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl rounded-2xl p-8 border border-orange-500/30">
              <h3 className="text-2xl font-bold text-white text-center mb-8">🏆 Témoignages de nos Partenaires</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mr-3">
                      <i className="ri-restaurant-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold">Le Petit Bistrot</h4>
                      <p className="text-gray-400 text-sm">Restaurant Paris 11ème</p>
                    </div>
                  </div>
                  <div className="flex mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-gray-300 text-sm italic">
                    "En 3 mois avec VYBE, notre restaurant est passé de 20 à 200 couverts par jour ! Les influenceurs ont créé un vrai buzz autour de nos plats."
                  </p>
                </div>

                <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mr-3">
                      <i className="ri-store-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold">Bella Mode</h4>
                      <p className="text-gray-400 text-sm">Boutique de mode</p>
                    </div>
                  </div>
                  <div className="flex mb-3">
                    {[...Array(4)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-400 text-sm"></i>
                    ))}
                    <i className="ri-star-line text-gray-400 text-sm"></i>
                  </div>
                  <p className="text-gray-300 text-sm italic">
                    "Les créateurs VYBE ont un engagement incroyable ! Nos ventes en ligne ont explosé grâce à leur contenu authentique."
                  </p>
                </div>

                <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mr-3">
                      <i className="ri-building-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold">TechStart</h4>
                      <p className="text-gray-400 text-sm">Startup tech</p>
                    </div>
                  </div>
                  <div className="flex mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-gray-300 text-sm italic">
                    "Grâce au Pack Premium, notre app a gagné 50K utilisateurs en 2 mois. Le ROI est exceptionnel !"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Collaborations avec les marques Section - DÉPLACÉE EN DERNIER */}
        <section className="py-20 px-4 bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 relative overflow-hidden">
          {/* Animated Background */}
          <div className="absolute inset-0">
            <div className="absolute top-20 left-10 w-32 h-32 bg-blue-500/20 rounded-full blur-xl animate-pulse"></div>
            <div className="absolute bottom-20 right-20 w-40 h-40 bg-purple-500/20 rounded-full blur-xl animate-pulse delay-1000"></div>
            <div className="absolute top-1/2 left-1/2 w-60 h-60 bg-indigo-500/10 rounded-full blur-2xl animate-pulse delay-500"></div>
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-full text-sm font-bold mb-6 animate-pulse">
                <i className="ri-building-line"></i>
                POUR LES MARQUES & RESTAURANTS
                <i className="ri-handshake-line"></i>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                <span className="text-blue-400">Collaborations</span> avec les Marques
              </h2>
              <p className="text-gray-300 max-w-2xl mx-auto">
                Vous êtes une marque ou un restaurant ? Collaborez avec notre agence VYBE pour booster votre visibilité
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold text-white mb-6">
                  Pourquoi choisir <span className="text-blue-400">VYBE Production</span> ?
                </h3>
                
                <div className="space-y-6">
                  {[
                    {
                      icon: 'ri-team-line',
                      title: 'Agence Complète',
                      description: 'Plus de 500 créateurs de contenu sélectionnés et formés',
                    },
                    {
                      icon: 'ri-trophy-line',
                      title: 'Résultats Prouvés',
                      description: 'ROI moyen de +300% pour nos clients marques',
                    },
                    {
                      icon: 'ri-shield-check-line',
                      title: 'Qualité Garantie',
                      description: 'Contenu professionnel et stratégie sur mesure',
                    },
                    {
                      icon: 'ri-global-line',
                      title: 'Portée Maximale',
                      description: 'Audience combinée de +50M de followers',
                    },
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className={`${feature.icon} text-blue-400 text-xl`}></i>
                      </div>
                      <div>
                        <h4 className="text-white font-semibold mb-2">{feature.title}</h4>
                        <p className="text-gray-300 text-sm">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setShowCollabForm(true)}
                  className="mt-8 px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
                >
                  <i className="ri-send-plane-line"></i>
                  Proposer une collaboration
                </button>
              </div>

              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Brand%20collaboration%20meeting%2C%20business%20partnership%20handshake%2C%20marketing%20agency%20presentation%2C%20professional%20business%20meeting%2C%20corporate%20collaboration&width=600&height=400&seq=brand-collaboration&orientation=landscape"
                  alt="Collaborations Marques"
                  className="w-full rounded-lg"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-lg"></div>
                
                {/* Stats overlay */}
                <div className="absolute bottom-4 left-4 right-4 bg-black/80 backdrop-blur-sm rounded-lg p-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-blue-400">500+</p>
                      <p className="text-white text-xs">Créateurs</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-purple-400">50M+</p>
                      <p className="text-white text-xs">Followers</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-400">300%</p>
                      <p className="text-white text-xs">ROI Moyen</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Services pour marques */}
            <div className="mt-16">
              <h3 className="text-2xl font-bold text-white text-center mb-8">
                Nos Services pour <span className="text-blue-400">Marques</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  {
                    icon: 'ri-camera-line',
                    title: 'Campagnes Créatives',
                    description: 'Contenu photo/vidéo sur mesure',
                    price: 'À partir de 2500€',
                  },
                  {
                    icon: 'ri-group-line',
                    title: 'Influence Marketing',
                    description: 'Collaborations avec nos créateurs',
                    price: 'À partir de 1500€',
                  },
                  {
                    icon: 'ri-calendar-event-line',
                    title: 'Événements',
                    description: 'Organisation et couverture',
                    price: 'À partir de 5000€',
                  },
                  {
                    icon: 'ri-line-chart-line',
                    title: 'Stratégie Complète',
                    description: 'Accompagnement marketing 360°',
                    price: 'Sur devis',
                  },
                ].map((service, index) => (
                  <div
                    key={index}
                    className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105"
                  >
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mb-4">
                      <i className={`${service.icon} text-white text-xl`}></i>
                    </div>
                    <h4 className="text-white font-semibold mb-2">{service.title}</h4>
                    <p className="text-gray-300 text-sm mb-3">{service.description}</p>
                    <p className="text-blue-400 font-semibold text-sm">{service.price}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Shooting Form Modal */}
      {showShootingForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-lg p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">Réserver un Shooting</h3>
              <button
                onClick={() => setShowShootingForm(false)}
                className="text-gray-400 hover:text-white"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form onSubmit={handleShootingSubmit} data-readdy-form>
              <div className="space-y-4">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">Nom complet *</label>
                  <input
                    type="text"
                    name="nom"
                    value={shootingFormData.nom}
                    onChange={(e) => setShootingFormData({ ...shootingFormData, nom: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-[#FF8A00] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={shootingFormData.email}
                    onChange={(e) => setShootingFormData({ ...shootingFormData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-[#FF8A00] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Téléphone *</label>
                  <input
                    type="tel"
                    name="telephone"
                    value={shootingFormData.telephone}
                    onChange={(e) => setShootingFormData({ ...shootingFormData, telephone: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-[#FF8A00] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Type de shooting</label>
                  <select
                    name="typeShoot"
                    value={shootingFormData.typeShoot}
                    onChange={(e) => setShootingFormData({ ...shootingFormData, typeShoot: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-[#FF8A00] focus:outline-none"
                  >
                    <option value="Standard">Standard - 180€</option>
                    <option value="Flying Dress">Flying Dress - 280€</option>
                    <option value="Couple / Lifestyle">Couple / Lifestyle - 250€</option>
                    <option value="Pack Luxe Créateur">Pack Luxe Créateur - 350€</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Date souhaitée</label>
                  <input
                    type="date"
                    name="date"
                    value={shootingFormData.date}
                    onChange={(e) => setShootingFormData({ ...shootingFormData, date: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-[#FF8A00] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Message (optionnel)</label>
                  <textarea
                    name="message"
                    value={shootingFormData.message}
                    onChange={(e) => setShootingFormData({ ...shootingFormData, message: e.target.value })}
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-[#FF8A00] focus:outline-none resize-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-6 px-6 py-3 bg-[#FF8A00] text-[#0A0A0A] font-semibold rounded-lg hover:bg-[#FF8A00]/90 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Collaboration Form Modal */}
      {showCollabForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-lg p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">Proposer une Collaboration</h3>
              <button
                onClick={() => setShowCollabForm(false)}
                className="text-gray-400 hover:text-white"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form onSubmit={handleCollabSubmit} data-readdy-form>
              <div className="space-y-4">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">Nom complet *</label>
                  <input
                    type="text"
                    name="nom"
                    value={collabFormData.nom}
                    onChange={(e) => setCollabFormData({ ...collabFormData, nom: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-[#FF8A00] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={collabFormData.email}
                    onChange={(e) => setCollabFormData({ ...collabFormData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-[#FF8A00] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Instagram *</label>
                  <input
                    type="text"
                    name="instagram"
                    value={(collabFormData as any).instagram || ''}
                    onChange={(e) => setCollabFormData({ ...collabFormData, instagram: e.target.value } as any)}
                    required
                    placeholder="@votre_compte"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-[#FF8A00] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Nombre de followers</label>
                  <select
                    name="followers"
                    value={(collabFormData as any).followers || ''}
                    onChange={(e) => setCollabFormData({ ...collabFormData, followers: e.target.value } as any)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border[#FF8A00] focus:outline-none"
                  >
                    <option value="">Sélectionner</option>
                    <option value="< 10K">&lt; 10K</option>
                    <option value="10K - 50K">10K - 50K</option>
                    <option value="50K - 200K">50K - 200K</option>
                    <option value="200K - 500K">200K - 500K</option>
                    <option value="> 500K">&gt; 500K</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Type de collaboration</label>
                  <select
                    name="typeCollab"
                    value={collabFormData.typeCollab}
                    onChange={(e) => setCollabFormData({ ...collabFormData, typeCollab: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-[#FF8A00] focus:outline-none"
                  >
                    <option value="Story Instagram">Story Instagram - 800€</option>
                    <option value="Post + Story">Post + Story - 1000€</option>
                    <option value="Vidéo Reel / TikTok">Vidéo Reel / TikTok - 1500€</option>
                    <option value="Mention multi-plateformes">Mention multi-plateformes - 2500€</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Budget proposé</label>
                  <input
                    type="text"
                    name="budget"
                    value={collabFormData.budget}
                    onChange={(e) => setCollabFormData({ ...collabFormData, budget: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-[#FF8A00] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Message (optionnel)</label>
                  <textarea
                    name="message"
                    value={collabFormData.message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setCollabFormData({ ...collabFormData, message: e.target.value });
                      }
                    }}
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-[#FF8A00] focus:outline-none resize-none"
                    placeholder={`Décrivez votre projet... (${collabFormData.message.length}/500)`}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !collabFormData.nom || !collabFormData.email || !(collabFormData as any).instagram}
                className="w-full mt-6 px-6 py-3 bg-[#FF8A00] text-[#0A0A0A] font-semibold rounded-lg hover:bg-[#FF8A00]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Envoi en cours...
                  </div>
                ) : (
                  'Envoyer la proposition'
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Subscription Form Modal */}
      {showSubscriptionForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-lg p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">Abonnement Marque/Restaurant</h3>
              <button
                onClick={() => setShowSubscriptionForm(false)}
                className="text-gray-400 hover:text-white"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubscriptionSubmit} data-readdy-form>
              <div className="space-y-4">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">Nom complet *</label>
                  <input
                    type="text"
                    name="nom"
                    value={subscriptionFormData.nom}
                    onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, nom: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-[#FF8A00] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={subscriptionFormData.email}
                    onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-[#FF8A00] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Entreprise/Restaurant *</label>
                  <input
                    type="text"
                    name="entreprise"
                    value={subscriptionFormData.entreprise}
                    onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, entreprise: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border[#FF8A00] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Secteur d'activité</label>
                  <select
                    name="secteur"
                    value={subscriptionFormData.secteur}
                    onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, secteur: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border[#FF8A00] focus:outline-none"
                  >
                    <option value="">Sélectionner</option>
                    <option value="Restaurant">Restaurant</option>
                    <option value="Mode">Mode</option>
                    <option value="Beauté">Beauté</option>
                    <option value="Lifestyle">Lifestyle</option>
                    <option value="Tech">Tech</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Pack choisi</label>
                  <select
                    name="pack"
                    value={subscriptionFormData.pack}
                    onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, pack: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border[#FF8A00] focus:outline-none"
                  >
                    <option value="Pack Starter">Pack Starter - 140€/mois</option>
                    <option value="Pack Business">Pack Business - 290€/mois</option>
                    <option value="Pack Premium">Pack Premium - 590€/mois</option>
                    <option value="Pack Élite">Pack Élite - 1200€/mois</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Message (optionnel)</label>
                  <textarea
                    name="message"
                    value={subscriptionFormData.message}
                    onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, message: e.target.value })}
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-[#FF8A00] focus:outline-none resize-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-6 px-6 py-3 bg-[#FF8A00] text-[#0A0A0A] font-semibold rounded-lg hover:bg-[#FF8A00]/90 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Submit Message */}
      {submitMessage && (
        <div className="fixed bottom-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          {submitMessage}
        </div>
      )}

      <Footer />
    </div>
  );
};

export default VybePage;
